import { useCallback } from 'react';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TEST_CONFIGURATION,
  TEST_OVERVIEW,
  TEST_MANAGEMENT_TESTS,
  TEST_CONDITIONAL_ALERT_DESTINATIONS,
  TEST_MANAGEMENT_CLOUD_TESTS,
  DESTINATIONS,
} from '@elementary/common-ui/constants/queryKeys';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as sharedApi from 'shared/services/api';
import * as api from '../services/api';
import {
  FetchTestConditionalAlertDestinationsApi,
  FetchTestConfigApi,
} from '../types';

function useTestConfiguration({ testId }: { testId?: string }) {
  const selectedEnvId = useSelectedEnv();
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const {
    data: testConfigData,
    isLoading: isTestConfigLoading,
    isFetching: isTestConfigFetching,
    error: testConfigError,
    refetch: refetchTestConfig,
  } = useGraphqlQuery<FetchTestConfigApi>({
    queryKey: [TEST_CONFIGURATION, testId, selectedEnvId],
    query: api.getTestConfiguration,
    variables: { id: testId },
    select: (testConfigData) => {
      if (
        testConfigData.test.config.type === 'anomaly_detection' ||
        testConfigData.test.config.type === 'freshness_anomaly_detection' ||
        testConfigData.test.config.type === 'pipeline_task_performance'
      ) {
        return {
          ...testConfigData,
          test: {
            ...testConfigData.test,
            config: {
              ...testConfigData.test.config,
              severity:
                String(testConfigData.test.config.severity).toLowerCase() ===
                'warning'
                  ? 1
                  : 10,
            },
          },
        };
      }

      return testConfigData;
    },
  });

  const {
    data: testConditionalAlertDestinationsData,
    isLoading: isTestConditionalAlertDestinationsLoading,
    isFetching: isTestConditionalAlertDestinationsFetching,
    error: testConditionalAlertDestinationsError,
    refetch: refetchTestConditionalAlertDestinations,
  } = useGraphqlQuery<FetchTestConditionalAlertDestinationsApi>({
    queryKey: [TEST_CONDITIONAL_ALERT_DESTINATIONS, testId, selectedEnvId],
    query: api.getTestConditionalAlertDestinations,
    variables: { id: testId },
  });

  const refetch = useCallback(() => {
    refetchTestConfig();
    refetchTestConditionalAlertDestinations();
  }, [refetchTestConfig, refetchTestConditionalAlertDestinations]);

  const invalidateQueriesMute = (selectedTestId: string) => {
    queryClient.invalidateQueries([TEST_CONFIGURATION, selectedTestId]);
    queryClient.invalidateQueries([TEST_OVERVIEW, selectedTestId]);
    queryClient.invalidateQueries([
      TEST_CONDITIONAL_ALERT_DESTINATIONS,
      testId,
      selectedEnvId,
    ]);
    queryClient.invalidateQueries([
      TEST_MANAGEMENT_CLOUD_TESTS,
      selectedEnvId,
      DESTINATIONS,
    ]);
    queryClient.invalidateQueries([TEST_MANAGEMENT_TESTS, selectedEnvId]);
  };

  const { mutateAsync: muteTest, isLoading: isMuteTestLoading } = useMutation(
    () => sharedApi.muteTest(selectedEnvId!, testId!),
    {
      onSuccess: () => {
        openSnackbar({
          variant: 'success',
          message: 'Test notifications disabled successfully',
        });
        invalidateQueriesMute(testId!);
      },

      onError: () =>
        openSnackbar({
          message: 'Test notification settings update failed',
          variant: 'error',
        }),
    },
  );

  const { mutateAsync: unMuteTest, isLoading: isUnMuteTestLoading } =
    useMutation(() => sharedApi.unMuteTest(selectedEnvId!, testId!), {
      onSuccess: () => {
        openSnackbar({
          message: 'Test notifications enabled successfully',
          variant: 'success',
        });
        invalidateQueriesMute(testId!);
      },
      onError: () =>
        openSnackbar({
          message: 'Test notification settings update failed',
          variant: 'error',
        }),
    });

  return {
    testConfigData,
    isTestConfigLoading,
    isTestConfigFetching,
    testConfigError,
    testConditionalAlertDestinationsData,
    isTestConditionalAlertDestinationsLoading,
    isTestConditionalAlertDestinationsFetching,
    testConditionalAlertDestinationsError,
    refetch,
    muteTest,
    unMuteTest,
    isMuteTestLoading: isMuteTestLoading || isUnMuteTestLoading,
  };
}

export default useTestConfiguration;
